import { cn } from '@/lib/utils'
import { ChevronDown, LayoutGrid } from 'lucide-react'
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@teach/ui'
import type { Category } from '@/types/course'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  categories: Category[] | undefined
  categoriesFilter: number[]
  language: string
  onToggle: (id: number) => void
  onClear: () => void
  className?: string
}

/**
 * Категории в Explore — раскрывающимся списком, в одном формате с выбором языка
 * (`LanguageFilterDropdown`). Выбор множественный; «Все» снимает отметки.
 */
export const CategoryFilterDropdown = ({
  categories,
  categoriesFilter,
  language,
  onToggle,
  onClear,
  className,
}: Props) => {
  const m = useMessages(messages)
  const nameOf = (c: Category) => (language === 'ru' ? c.nameRu : c.nameEn)
  const picked = (categories ?? []).filter((c) => categoriesFilter.includes(c.id))
  const label =
    picked.length === 0
      ? m.allCategories
      : picked.length === 1
        ? nameOf(picked[0])
        : m.categoriesCount(picked.length)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Уже `lg` — квадратная кнопка-иконка; выбранные разделы подсвечивают иконку. */}
        <Button
          variant="outline"
          aria-label={label}
          className={cn('w-14 shrink-0 px-0 lg:w-auto lg:px-4', className)}
        >
          <LayoutGrid
            className={cn(
              'size-5 lg:mr-2 lg:size-4',
              picked.length > 0 ? 'text-brand' : 'text-muted-foreground',
            )}
          />
          <span className="hidden max-w-40 truncate lg:inline">{label}</span>
          <ChevronDown className="ml-2 hidden size-4 lg:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 w-56 overflow-y-auto [&>*+*]:mt-0.5">
        <DropdownMenuCheckboxItem
          checked={categoriesFilter.length === 0}
          onCheckedChange={onClear}
          onSelect={(e) => e.preventDefault()}
        >
          {m.allCategories}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {categories?.map((c) => (
          <DropdownMenuCheckboxItem
            key={c.id}
            checked={categoriesFilter.includes(c.id)}
            onCheckedChange={() => onToggle(c.id)}
            onSelect={(e) => e.preventDefault()}
          >
            {nameOf(c)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
